import { getHeader, uploadAudioFileDetails } from './apiOptions';
import { fetchData, postData } from './apiService';
import { apiUrl, REACT_APP_WEBSOCKET_URL, fileKey, uploadFile } from '../constants/constants';
import { getFormattedDate, getSpeakingTestFormattedDate } from '../service/utils';
import { decryptURL } from '../service/DecryptionService';
import { InsperaObject, SpeakCheckScreenDetailsObject, SpeedObject, settingsObject } from '../interface/interface';

/**
 * @params accessToken
 * @return the result of token validation, and if error then it will send true else false
 */
export const validateInsperaAccessToken = async (payload: any) => {
    const { accessToken } = payload;
    try {
        const response = await postData(
            apiUrl.VALIDATE_INSPERA_ACCESS_TOKEN_API_URL,
            { accessToken },
            { headers: { ...getHeader() } },
            'icmst'
        );
        return {
            error: !response?.data,
            ...response,
        };
    } catch (error) {
        console.error('Error fetching access token: ', error);
        return {
            error: true,
            errorMessage: error,
        };
    }
};

// Get data from the API using the getHeader function
export const getData = async () => {
    try {
        const data = await fetchData(apiUrl.GET_P1_QUESTIONS, { headers: getHeader() }, 'cmst', {});
        // Removed debug console log
    } catch (error) {
        console.error('Error fetching data:', error);
    }
};

// Post data to the API using the postHeader function
export const uploadAudioFile = async (fileBlob: Blob, fileFormat: string, fileName: string, testTakerDetails: any , insperaDetails: any) => {
    try {
        let data = uploadAudioFileDetails(fileBlob, fileFormat || '.mp3', fileName || '', testTakerDetails,insperaDetails);
        return await postData(
            apiUrl.UPLOAD_AUDIO_FILE,
            data,
            { headers: { Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'cmst'
        );
    } catch (error) {
        console.error('Error posting data:', error);
    }
};

export const getContentData = async (testTakerDetails: any, urlParams: any) => {
    try {
        const response = await postData(
            `${apiUrl.GET_CONTENT_DATA}${urlParams? '?'+urlParams : ''}`,
            {
                testTakerId: testTakerDetails?.testTakerId,
                centreName: testTakerDetails?.testTakerCenterName,
                langAccent: testTakerDetails?.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
                ratingScore: ''
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'atc'
        );
        return {
            ...response?.data,
            status: response?.status,
        };
    } catch (error) {
        console.error('Error fetching content data:', error);
        throw error;
    }
};

export const getLiteVersionContentData = async (testTakerDetails: any) => {
    try {
        const response = await postData(
            apiUrl.GET_INT_SUITE_CONTENT,
            {
                testTakerId: testTakerDetails?.testTakerId,
                centreName: testTakerDetails?.testTakerCenterName,
                langAccent: testTakerDetails?.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'atc'
        );
        return {
            ...response?.data,
            status: response?.status,
        };
    } catch (error) {
        console.error('Error fetching content data:', error);
        throw error;
    }
};

export const getExtendedContentData = async (testTakerDetails: any, urlParams: any) => {
    try {
        const response = await postData(
            `${apiUrl.GET_EXTENDED_CONTENT}${urlParams? '?'+urlParams : ''}`,
            {
                testTakerId: testTakerDetails?.testTakerId,
                centreName: testTakerDetails?.testTakerCenterName,
                langAccent: testTakerDetails?.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
                ratingScore: '',
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'atc'
        );
        return {
            ...response?.data,
            status: response?.status,
        };
    } catch (error) {
        console.error('Error fetching content data:', error);
    }
};

export const getProbes = async (testTakerDetails: any, urlParams: any) => {
    try {
        const response = await postData(
            `${apiUrl.GET_PROBES}${urlParams? '?'+urlParams : ''}`,
            {
                testTakerId: testTakerDetails?.testTakerId,
                centreName: testTakerDetails?.testTakerCenterName,
                langAccent: testTakerDetails?.testTakerLanguage,
                testTakerDate: getFormattedDate(new Date()),
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'atc'
        );

        // Decrypt URLs and attach to the items
        const probesData = response.data;
        const probes = probesData.Probes;

        for (const probe of probes) {
            probe.audioPreSignUrl = await decryptURL(probe.audioPreSignUrl);
        }

        return {
            ...probesData,
            status: response.status,
        };
    } catch (error) {
        console.error('Error fetching probe data:', error);
    }
};

export const fetchTestTakerSessionId = async (testTakerAccessToken: any, username: any) => {
    /**
     * Passsing username as a query param to get unique session id
     */
    try {
        return await fetchData(
            apiUrl.GET_SESSION_ID_URL + '?loginInUser=' + username,
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerAccessToken } },
            'cmst',
            {}
        );
    } catch (error) {
        console.error('Error fetching fetchTestTakerSessionId:', error);
    }
};

export const connectToIntentAudioSocket = (testTaker: any) => {
    let socket = new WebSocket(
        `${REACT_APP_WEBSOCKET_URL}${apiUrl.INTENT_AUDIO_DETECTION}`
        // ["Authorization", testTaker?.testTakerAccessToken]
    );
    return socket;
};

export const getRating = () => {
    /* This part will be replaced once we integrated LEX. Till that we have to fetch Medium type
  in Part 3 as per story 859. Once lex is integarted, score will be generated through api. It will return 
  value between 0-10 .  if we receive less than 5,  medium will be played . If it is greater than or equal to
  5 , we will play  hard type question in  part 3. As of now to play medium hardcoded as 4*/
    let ratingScore = 6;
    return ratingScore;
};
export const saveSpeakingTestProgressionData = async (
    testTakerDetails: any = undefined,
    playedAudioFilesDetails: any = [],
    status: string = 'Test Completed',
    fullRecordingFilePath: string = '',
    logId: string = '',
    insperaDetails: InsperaObject = { assessmentId: '', externalId: '', partner: '', product: '' },
    speedDetails: SpeedObject = { uploadSpeed: '', downloadSpeed: '' },
    settings: settingsObject = { fontSize: '', colourContrast: '' },
    speakCheckDetails: SpeakCheckScreenDetailsObject = { duration: 0, responseFilePath: ''}
) => {
    try {
        const response = await postData(
            apiUrl.SPEAKING_TEST_PROGRESSION_DATA,
            {
                id: logId,
                testTakerId: testTakerDetails?.testTakerId,
                uniqueSessionId: testTakerDetails?.testTakerSessionId,
                centerName: testTakerDetails?.testTakerCenterName,
                testDate: getSpeakingTestFormattedDate().utc,
                testCentreTimeZone: getSpeakingTestFormattedDate().localTimezone,
                language: testTakerDetails?.testTakerLanguage,
                status: status,
                playedAudioFilesDetails: playedAudioFilesDetails,
                fullAudioRecordingFilePath: fullRecordingFilePath,
                ...insperaDetails,
                uploadSpeed: speedDetails?.uploadSpeed,
                downloadSpeed: speedDetails?.downloadSpeed,
                settings: settings,
                preCheck: {
                    speakCheckScreen: speakCheckDetails,
                }
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'cmst'
        );
        return {
            data: response?.data,
            status: response.status,
        };
    } catch (error) {
        console.error('Error while saving progression test data:', error);
        throw error;
    }
};

export const fetchHealthCheck = async () => {
    const serviceResponse = await fetchData(apiUrl.HEALTH_CHECK, { headers: { ...getHeader() } }, 'cmst');
    const atcResponse = await fetchData(apiUrl.HEALTH_CHECK, { headers: { ...getHeader() } }, 'atc');

    return {
        service: serviceResponse.status !== 200 ? null : serviceResponse,
        atc: atcResponse.status !== 200 ? null : atcResponse,
    };
};

export const fetchIntentHealthCheck = async ( uniqueSessionId: any, testTakerAccessToken: any) => {
    const lexResponse = await fetchData(
        `${apiUrl.INTENT_HEALTH_CHECK}?uniqueSessionId=${uniqueSessionId}`, 
        { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerAccessToken } }, 
        'cmst'
    );
    return {
        service: lexResponse.status !== 200 ? false : lexResponse,
    };
}

export const fetchTestRules = async (testTakerDetails: any) => {
    const response = await fetchData(
        `${apiUrl.TEST_RULES}?productType=GT`,
        {
            headers: {
                Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken,
                ...getHeader(),
            },
        },
        'cmst'
    );
    return response.data;
};

/**
 * @params takes a json object to send message to parent window
 */
export const sendMessageToInsperaIB = (payload: any) => {
    // Replace '*' with the iframe origin for better security.
    const finalPayload = JSON.parse(JSON.stringify(payload));
    window.parent.postMessage(finalPayload, `*`);
};

// calling downloadfile api
export const getDownloadFile = async (uniqueSessionId: any, testTakerId: any, testTakerAccessToken: string) => {
    try {
        const data = await fetchData(
            apiUrl.DOWNLOAD_AUDIO_FILE +
                '?testTakerId=' +
                testTakerId +
                '&uniqueSessionId=' +
                encodeURIComponent(uniqueSessionId) +
                '&fileKey=' +
                fileKey,
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerAccessToken } },
            'cmst',
            {}
        );
        return data;
    } catch (error) {
        console.error('Error fetching data:', error);
    }
};

//API for Precheck logs 
export const savePrecheckLogs = async ( testTakerDetails: any = undefined,payload:any) => {
    try {
        const response = await postData(
            apiUrl.PRECHECK_LOGS,
                payload,
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'cmst'
        );
        return {
            data: response?.data,
           
        };
    } catch (error: any) {
          let errorData;
        if (error.response) {
            errorData = error.response.data;
             console.error('Error while saving precheck data:', error);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
       
    }
};